import { PropsWithChildren } from 'react';
import { Icon } from '@antimatterhq/ui/components/icon';
import { Text } from '@antimatterhq/ui/components/typography';
import { cn } from '@/lib/utils';

export type FormMessageProps = {
  type: 'error' | 'success' | 'warning';
  className?: string;
  'data-testid'?: string;
  hideIcon?: boolean;
} & PropsWithChildren;

const iconMap = {
  error: {
    icon: 'lucide:octagon-alert',
    class: 'text-red-600 dark:text-red-500',
  },
  success: {
    icon: 'lucide:circle-check',
    class: 'text-green-600 dark:text-green-500',
  },
  warning: {
    icon: 'lucide:triangle-alert',
    class: 'text-yellow-600 dark:text-yellow-500',
  },
} as const;

export default function FormMessage({
  type,
  className,
  children,
  'data-testid': testId,
  hideIcon,
}: FormMessageProps) {
  const { icon, class: colorClass } = iconMap[type];

  return (
    <span
      className={cn('flex items-start gap-1', colorClass, className)}
      data-testid={testId || `form-message-${type}`}
      aria-label={type}
    >
      {!hideIcon && (
        <Icon
          icon={icon}
          height="1.2rem"
          width="1.2rem"
          className="mt-[1px] shrink-0"
          data-testid={testId ? `${testId}-icon` : `form-message-${type}-icon`}
        />
      )}
      <Text
        color="inherit"
        bold
        className="whitespace-pre text-sm"
        data-testid={testId ? `${testId}-text` : `form-message-${type}-text`}
      >
        {children}
      </Text>
    </span>
  );
}
